import { motion } from 'framer-motion';
import React from 'react';
import { FiArrowLeft, FiEye, FiPackage, FiShoppingBag, FiShoppingCart, FiSmartphone, FiTrendingUp } from 'react-icons/fi';
import { Link } from 'react-router-dom';
import Footer from '../../components/Footer';
import Navbar from '../../components/Navbar';

const EcommerceRetail = () => {
  const solutions = [
    {
      title: "E-commerce Platform Development",
      description: "Custom e-commerce solutions with advanced features, payment integration, and scalable architecture for growing businesses.",
      icon: FiShoppingCart,
      features: ["Multi-vendor Support", "Payment Gateway Integration", "Inventory Management", "Order Processing"]
    },
    {
      title: "Mobile App Development",
      description: "Native and cross-platform mobile applications for iOS and Android with seamless shopping experiences.",
      icon: FiSmartphone,
      features: ["Native Apps", "Cross-platform Solutions", "Push Notifications", "Offline Capabilities"]
    },
    {
      title: "AI-Powered Recommendation Systems",
      description: "Machine learning algorithms that personalize shopping experiences and increase conversion rates.",
      icon: FiEye,
      features: ["Personalization Engine", "Behavioral Analytics", "Product Recommendations", "Dynamic Pricing"]
    },
    {
      title: "Inventory & Order Management",
      description: "Comprehensive systems for managing inventory, orders, and fulfillment across multiple channels.",
      icon: FiPackage,
      features: ["Real-time Inventory", "Multi-channel Integration", "Automated Fulfillment", "Supply Chain Optimization"]
    }
  ];

  const caseStudies = [
    {
      title: "Multi-vendor E-commerce Platform",
      challenge: "Retail company needed a scalable platform to support 500+ vendors and handle high traffic volumes",
      solution: "Built cloud-native e-commerce platform with microservices architecture and AI-powered recommendations",
      results: ["300% increase in vendor onboarding", "75% improvement in conversion rates", "99.9% platform uptime during peak sales"]
    },
    {
      title: "Fashion Retail Mobile App",
      challenge: "Fashion brand required engaging mobile shopping experience with AR try-on capabilities",
      solution: "Developed native mobile app with AR integration, social features, and personalized recommendations",
      results: ["500k+ app downloads", "40% increase in mobile sales", "85% user retention rate"]
    }
  ];

  const platforms = [
    { name: "Shopify Plus", percentage: 30 },
    { name: "Magento Commerce", percentage: 25 },
    { name: "Custom Solutions", percentage: 35 },
    { name: "Headless Commerce", percentage: 10 }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-yellow-50 via-orange-50 to-yellow-100">
      <Navbar />
      
      {/* Hero Section */}
      <section className="pt-20 relative overflow-hidden">
        <div className="relative h-96 md:h-[500px] bg-gradient-to-br from-yellow-500 to-orange-600">
          <div className="absolute inset-0 bg-black/20"></div>
          <div className="absolute inset-0 bg-gradient-to-r from-yellow-500/80 to-orange-600/60"></div>
          
          <div className="absolute bottom-0 left-0 right-0 p-8 md:p-16">
            <div className="max-w-7xl mx-auto">
              <motion.div
                initial={{ opacity: 0, y: 50 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8 }}
                className="space-y-6"
              >
                <div className="flex items-center space-x-4 text-white/80 text-sm font-body">
                  <Link 
                    to="/expertise"
                    className="flex items-center space-x-2 hover:text-white transition-colors duration-300"
                  >
                    <FiArrowLeft size={16} />
                    <span>Back to Expertise</span>
                  </Link>
                  <span>•</span>
                  <span className="px-3 py-1 bg-white/20 rounded-full font-accent tracking-wide">
                    E-commerce & Retail
                  </span>
                </div>
                
                <div className="flex items-center space-x-4 mb-4">
                  <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center">
                    <FiShoppingCart className="text-white" size={32} />
                  </div>
                  <div>
                    <h1 className="text-4xl md:text-6xl font-bold text-white leading-tight font-display tracking-tight">
                      E-commerce & Retail
                    </h1>
                    <p className="text-xl text-yellow-100 font-body font-light tracking-wide">
                      Building Next-Generation Shopping Experiences
                    </p>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* Overview Section */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <span className="inline-block px-4 py-2 bg-yellow-100 text-yellow-700 rounded-full text-sm font-medium mb-6 font-accent tracking-wide">
                Retail Innovation
              </span>
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6 font-display tracking-tight">
                Transforming
                <span className="block bg-gradient-to-r from-yellow-600 to-orange-600 bg-clip-text text-transparent">
                  Retail Experiences
                </span>
              </h2>
              <p className="text-xl text-gray-600 mb-8 font-body font-light leading-relaxed">
                We create cutting-edge e-commerce platforms and retail solutions that enhance customer experiences, 
                drive sales growth, and help businesses thrive in the digital marketplace.
              </p>
              <div className="grid grid-cols-2 gap-6">
                <div className="text-center p-4 bg-white rounded-2xl shadow-lg border border-yellow-100">
                  <div className="text-3xl font-bold text-yellow-600 font-display">25+</div>
                  <div className="text-sm text-gray-600 font-body">E-commerce Projects</div>
                </div>
                <div className="text-center p-4 bg-white rounded-2xl shadow-lg border border-yellow-100">
                  <div className="text-3xl font-bold text-orange-600 font-display">1M+</div>
                  <div className="text-sm text-gray-600 font-body">Transactions Processed</div>
                </div>
                <div className="text-center p-4 bg-white rounded-2xl shadow-lg border border-yellow-100">
                  <div className="text-3xl font-bold text-yellow-600 font-display">40+</div>
                  <div className="text-sm text-gray-600 font-body">Retail Partners</div>
                </div>
                <div className="text-center p-4 bg-white rounded-2xl shadow-lg border border-yellow-100">
                  <div className="text-3xl font-bold text-orange-600 font-display">99.5%</div>
                  <div className="text-sm text-gray-600 font-body">Uptime Guarantee</div>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="space-y-6"
            >
              {[
                { icon: FiShoppingBag, title: "Omnichannel Commerce", desc: "Seamless shopping experiences across all touchpoints and channels" },
                { icon: FiTrendingUp, title: "Conversion Optimization", desc: "Data-driven strategies to maximize sales and customer retention" },
                { icon: FiEye, title: "AR/VR Integration", desc: "Immersive shopping experiences with cutting-edge technology" }
              ].map((benefit, index) => (
                <div key={index} className="flex items-start space-x-4 p-6 bg-white rounded-2xl shadow-lg border border-yellow-100">
                  <div className="w-12 h-12 bg-gradient-to-br from-yellow-500 to-orange-600 rounded-xl flex items-center justify-center flex-shrink-0">
                    <benefit.icon className="text-white" size={24} />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2 font-heading tracking-wide">
                      {benefit.title}
                    </h3>
                    <p className="text-gray-600 font-body leading-relaxed">
                      {benefit.desc}
                    </p>
                  </div>
                </div>
              ))}
            </motion.div>
          </div>
        </div>
      </section>

      {/* Platform Expertise Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6 font-display tracking-tight">
              Platform
              <span className="block bg-gradient-to-r from-yellow-600 to-orange-600 bg-clip-text text-transparent">
                Expertise
              </span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto font-body font-light tracking-wide leading-relaxed">
              We work with leading e-commerce platforms and create custom solutions tailored to your specific business needs.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {platforms.map((platform, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1, duration: 0.6 }}
                viewport={{ once: true }}
                className="text-center p-6 bg-gradient-to-br from-yellow-50 to-orange-50 rounded-3xl border border-yellow-100"
              >
                <div className="relative w-24 h-24 mx-auto mb-4">
                  <svg className="w-24 h-24 transform -rotate-90" viewBox="0 0 36 36">
                    <path
                      d="m18,2.0845 a 15.9155,15.9155 0 0,1 0,31.831 a 15.9155,15.9155 0 0,1 0,-31.831"
                      fill="none"
                      stroke="#e5e7eb"
                      strokeWidth="2"
                    />
                    <path
                      d="m18,2.0845 a 15.9155,15.9155 0 0,1 0,31.831 a 15.9155,15.9155 0 0,1 0,-31.831"
                      fill="none"
                      stroke="url(#yellowGradient)"
                      strokeWidth="2"
                      strokeDasharray={`${platform.percentage}, 100`}
                    />
                    <defs>
                      <linearGradient id="yellowGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                        <stop offset="0%" stopColor="#eab308" />
                        <stop offset="100%" stopColor="#ea580c" />
                      </linearGradient>
                    </defs>
                  </svg>
                  <div className="absolute inset-0 flex items-center justify-center">
                    <span className="text-2xl font-bold text-yellow-600 font-display">{platform.percentage}%</span>
                  </div>
                </div>
                <h3 className="text-lg font-bold text-gray-900 font-heading tracking-wide">
                  {platform.name}
                </h3>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Solutions Section */}
      <section className="py-20 bg-gradient-to-br from-yellow-50 to-orange-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6 font-display tracking-tight">
              E-commerce
              <span className="block bg-gradient-to-r from-yellow-600 to-orange-600 bg-clip-text text-transparent">
                Solutions
              </span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto font-body font-light tracking-wide leading-relaxed">
              Comprehensive e-commerce and retail technology solutions designed to drive growth and enhance customer satisfaction.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8">
            {solutions.map((solution, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1, duration: 0.6 }}
                viewport={{ once: true }}
                className="group p-8 bg-white rounded-3xl border border-yellow-100 hover:shadow-2xl transition-all duration-500"
              >
                <div className="flex items-start space-x-4 mb-6">
                  <div className="w-14 h-14 bg-gradient-to-br from-yellow-500 to-orange-600 rounded-2xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                    <solution.icon className="text-white" size={28} />
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-gray-900 mb-3 font-heading tracking-wide">
                      {solution.title}
                    </h3>
                    <p className="text-gray-700 font-body leading-relaxed">
                      {solution.description}
                    </p>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  {solution.features.map((feature, idx) => (
                    <div key={idx} className="flex items-center space-x-2">
                      <div className="w-2 h-2 bg-gradient-to-r from-yellow-500 to-orange-600 rounded-full"></div>
                      <span className="text-sm text-gray-700 font-body">{feature}</span>
                    </div>
                  ))}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Case Studies Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6 font-display tracking-tight">
              Retail
              <span className="block bg-gradient-to-r from-yellow-600 to-orange-600 bg-clip-text text-transparent">
                Success Stories
              </span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto font-body font-light tracking-wide leading-relaxed">
              Real-world examples of how our e-commerce solutions have transformed retail businesses and driven exceptional results.
            </p>
          </motion.div>

          <div className="grid lg:grid-cols-2 gap-8">
            {caseStudies.map((study, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.2, duration: 0.8 }}
                viewport={{ once: true }}
                className="bg-gradient-to-br from-yellow-50 to-orange-50 p-8 rounded-3xl border border-yellow-100"
              >
                <h3 className="text-2xl font-bold text-gray-900 mb-4 font-heading tracking-wide">
                  {study.title}
                </h3>
                
                <div className="space-y-6">
                  <div>
                    <h4 className="text-lg font-semibold text-gray-900 mb-2 font-heading tracking-wide">Challenge</h4>
                    <p className="text-gray-600 font-body leading-relaxed">{study.challenge}</p>
                  </div>
                  
                  <div>
                    <h4 className="text-lg font-semibold text-gray-900 mb-2 font-heading tracking-wide">Solution</h4>
                    <p className="text-gray-600 font-body leading-relaxed">{study.solution}</p>
                  </div>
                  
                  <div>
                    <h4 className="text-lg font-semibold text-gray-900 mb-3 font-heading tracking-wide">Results</h4>
                    <div className="space-y-2">
                      {study.results.map((result, idx) => (
                        <div key={idx} className="flex items-center space-x-3">
                          <div className="w-2 h-2 bg-gradient-to-r from-yellow-500 to-orange-600 rounded-full"></div>
                          <span className="text-gray-700 font-body">{result}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-yellow-500 to-orange-600 relative overflow-hidden">
        <div className="absolute inset-0 bg-white/5"></div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="space-y-8"
          >
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6 font-display tracking-tight">
              Ready to Transform Your E-commerce Business?
            </h2>
            <p className="text-xl text-yellow-100 max-w-3xl mx-auto font-body font-light tracking-wide leading-relaxed">
              Let's discuss how our e-commerce expertise can help you create exceptional shopping experiences and drive sales growth.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="bg-white text-yellow-600 px-8 py-4 rounded-full font-semibold text-lg shadow-xl hover:shadow-2xl transition-all duration-300 font-accent tracking-wide"
                >
                  Start Your E-commerce Project
                </motion.button>
              </Link>
              <Link to="/contact">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="border-2 border-white text-white px-8 py-4 rounded-full font-semibold text-lg hover:bg-white hover:text-yellow-600 transition-all duration-300 font-accent tracking-wide"
              >
                Schedule Consultation
              </motion.button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default EcommerceRetail; 